import { useState } from 'react';

import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Spinner } from '../../../components/ui/spinner';
import { Switch } from '../../../components/ui/switch';
import { useInviteAdminUser } from '../../../hooks/admin/useAdminUsers';
import { classifyError } from '../../../lib/errors';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const invite = useInviteAdminUser();

  const valid = EMAIL_REGEX.test(email) && nome.trim().length >= 2;

  const handleClose = () => {
    onOpenChange(false);
    setEmail('');
    setNome('');
    setIsAdmin(false);
    invite.reset();
  };

  const handleSubmit = () => {
    if (!valid) return;
    invite.mutate(
      { email: email.trim(), nome: nome.trim(), is_admin: isAdmin },
      { onSuccess: handleClose },
    );
  };

  const errorMessage = invite.isError ? classifyError(invite.error).message : null;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convidar Usuário</DialogTitle>
          <DialogDescription>
            O convidado receberá um email com link para definir a própria senha.
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@exemplo.com"
              disabled={invite.isPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-nome">Nome</Label>
            <Input
              id="invite-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome completo"
              disabled={invite.isPending}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <Label htmlFor="invite-admin" className="cursor-pointer">
                Atribuir papel de administrador
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                O usuário poderá gerenciar planos, cortesias e outros usuários.
              </p>
            </div>
            <Switch
              id="invite-admin"
              checked={isAdmin}
              onCheckedChange={setIsAdmin}
              disabled={invite.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={invite.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || invite.isPending}>
            {invite.isPending ? (
              <>
                <Spinner size={16} className="text-inherit" /> Enviando…
              </>
            ) : (
              'Enviar Convite'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InviteUserDialog;
